import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Product, AllProductsResponse } from '../types';
import { api } from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CompleteProductList: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [orderBy, setOrderBy] = useState<string>('nome');
    const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [page, setPage] = useState(1);
    const [sizeItems, setSizeItems] = useState(10);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        size_items: 10,
        total_pages: 1
    });
    const [searchInput, setSearchInput] = useState('');

    const loadProducts = useCallback(async () => {
        try {
            setIsLoading(true);
            const response: AllProductsResponse = await api.getAllProducts({
                search,
                orderBy,
                orderDirection,
                page,
                size_items: sizeItems
            });
            // Garantir que products seja sempre um array
            setProducts(response?.products || []);
            setPagination(response?.pagination || {
                total: 0,
                page: 1,
                size_items: sizeItems,
                total_pages: 1
            });
        } catch (error) {
            console.error('Erro ao carregar produtos:', error);
            // Garantir arrays vazios em caso de erro
            setProducts([]);
            setPagination({
                total: 0,
                page: 1,
                size_items: sizeItems,
                total_pages: 1
            });
            alert('Erro ao carregar produtos. Verifique se o backend está rodando.');
        } finally {
            setIsLoading(false);
        }
    }, [search, orderBy, orderDirection, page, sizeItems]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const handleSearch = () => {
        setSearch(searchInput);
        setPage(1); // Resetar para primeira página ao buscar
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    const handleOrderChange = (field: string) => {
        if (orderBy === field) {
            setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setOrderBy(field);
            setOrderDirection('ASC');
        }
        setPage(1);
    };

    const handleSizeChange = (newSize: number) => {
        setSizeItems(newSize);
        setPage(1);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const exportToPDF = async () => {
        try {
            // Buscar todos os dados com os mesmos filtros aplicados
            const response: AllProductsResponse = await api.getAllProducts({
                search,
                orderBy,
                orderDirection,
                page: 1,
                size_items: pagination.total || 1000 // Buscar todos os registros
            });

            const allProducts = response?.products || [];
            const doc = new jsPDF();

            // Título
            doc.setFontSize(18);
            doc.text('Lista Completa de Produtos', 14, 20);

            // Filtros aplicados
            doc.setFontSize(10);
            let yPos = 30;
            doc.text(`Total de registros: ${pagination.total || allProducts.length}`, 14, yPos);
            yPos += 5;
            doc.text(`Data de exportação: ${new Date().toLocaleString('pt-BR')}`, 14, yPos);
            yPos += 10;

            // Preparar dados da tabela
            const tableData = allProducts.map(product => [
                product.nome || '-',
                product.disponivel ? 'Disponível' : 'Indisponível',
                product.nome_responsavel || '-',
                product.telefone_responsavel || '-',
                formatDate(product.updated_at)
            ]);

            // Criar tabela usando autoTable
            autoTable(doc, {
                head: [['Nome', 'Status', 'Responsável', 'Telefone', 'Data Atualização']],
                body: tableData,
                startY: yPos,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [63, 62, 34] },
                alternateRowStyles: { fillColor: [253, 253, 234] },
                margin: { top: yPos }
            });

            // Salvar PDF
            doc.save(`lista-produtos-${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Erro ao exportar PDF:', error);
            alert('Erro ao exportar PDF. Tente novamente.');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-primary py-8 px-4">
            <div className="container mx-auto max-w-7xl">
                {/* Link de volta */}
                <div className="flex justify-start mb-4">
                    <Link
                        to="/"
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                    >
                        ← Voltar para Home
                    </Link>
                </div>

                {/* Cabeçalho */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-3">
                        Lista Completa de Produtos
                    </h1>
                    <div className="w-32 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto rounded-full mb-4"></div>
                </div>

                {/* Filtros e Controles */}
                <div className="bg-gradient-card backdrop-blur-sm rounded-2xl shadow-soft-lg p-6 mb-6 border border-white/50">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        {/* Busca */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Buscar
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    placeholder="Buscar por nome, responsável ou telefone..."
                                    className="flex-1 px-4 py-2 rounded-lg border border-white/50 bg-white/50 text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <button
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                >
                                    🔍
                                </button>
                                {search && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Ordenação */}
                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Ordenar por
                            </label>
                            <select
                                value={orderBy}
                                onChange={(e) => handleOrderChange(e.target.value)}
                                aria-label="Ordenar por"
                                className="w-full px-4 py-2 rounded-lg border border-white/50 bg-white/50 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="nome">Nome</option>
                                <option value="disponivel">Status</option>
                                <option value="nome_responsavel">Responsável</option>
                                <option value="updated_at">Data Atualização</option>
                            </select>
                        </div>

                        {/* Direção de Ordenação */}
                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Direção
                            </label>
                            <button
                                onClick={() => setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC')}
                                className="w-full px-4 py-2 rounded-lg border border-white/50 bg-white/50 text-text-primary hover:bg-white/70 transition-colors flex items-center justify-center gap-2"
                            >
                                {orderDirection === 'ASC' ? '⬆️ Ascendente' : '⬇️ Descendente'}
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-4">
                        {/* Botão Exportar PDF */}
                        <button
                            onClick={exportToPDF}
                            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                        >
                            📄 Exportar PDF
                        </button>
                    </div>
                </div>

                {/* Tabela */}
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-700"></div>
                        <p className="text-text-secondary text-lg mt-4">Carregando produtos...</p>
                    </div>
                ) : (
                    <div className="bg-gradient-card backdrop-blur-sm rounded-2xl shadow-soft-lg border border-white/50 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-primary-600 text-white">
                                        <th
                                            className="px-6 py-4 text-left font-semibold cursor-pointer hover:bg-primary-700 transition-colors"
                                            onClick={() => handleOrderChange('nome')}
                                        >
                                            Nome {orderBy === 'nome' && (orderDirection === 'ASC' ? '⬆️' : '⬇️')}
                                        </th>
                                        <th
                                            className="px-6 py-4 text-left font-semibold cursor-pointer hover:bg-primary-700 transition-colors"
                                            onClick={() => handleOrderChange('disponivel')}
                                        >
                                            Status {orderBy === 'disponivel' && (orderDirection === 'ASC' ? '⬆️' : '⬇️')}
                                        </th>
                                        <th
                                            className="px-6 py-4 text-left font-semibold cursor-pointer hover:bg-primary-700 transition-colors"
                                            onClick={() => handleOrderChange('nome_responsavel')}
                                        >
                                            Responsável {orderBy === 'nome_responsavel' && (orderDirection === 'ASC' ? '⬆️' : '⬇️')}
                                        </th>
                                        <th className="px-6 py-4 text-left font-semibold">Telefone</th>
                                        <th
                                            className="px-6 py-4 text-left font-semibold cursor-pointer hover:bg-primary-700 transition-colors"
                                            onClick={() => handleOrderChange('updated_at')}
                                        >
                                            Data Atualização {orderBy === 'updated_at' && (orderDirection === 'ASC' ? '⬆️' : '⬇️')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!products || products.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-text-secondary">
                                                Nenhum produto encontrado
                                            </td>
                                        </tr>
                                    ) : (
                                        products.map((product, index) => (
                                            <tr
                                                key={product.id}
                                                className={`border-b border-white/20 ${index % 2 === 0 ? 'bg-white/30' : 'bg-white/10'
                                                    } hover:bg-white/50 transition-colors`}
                                            >
                                                <td className="px-6 py-4 text-text-primary font-medium">{product.nome}</td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${product.disponivel
                                                            ? 'bg-primary-100 text-primary-800 border border-primary-300'
                                                            : 'bg-red-100 text-red-800 border border-red-300'
                                                            }`}
                                                    >
                                                        {product.disponivel ? '✓ Disponível' : '✗ Indisponível'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-text-primary">
                                                    {product.nome_responsavel || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-text-primary">
                                                    {product.telefone_responsavel || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-text-secondary">
                                                    {formatDate(product.updated_at)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Paginação e Controles */}
                {!isLoading && (
                    <div className="mt-6 bg-gradient-card backdrop-blur-sm rounded-2xl shadow-soft-lg p-4 border border-white/50">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            {/* Informação de registros */}
                            <div className="text-text-primary">
                                Mostrando {products.length > 0 ? (page - 1) * sizeItems + 1 : 0} a{' '}
                                {Math.min(page * sizeItems, pagination.total)} de {pagination.total} produtos
                            </div>

                            <div className="flex flex-wrap items-center gap-4">
                                {/* Itens por página */}
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-text-primary">
                                        Itens por página:
                                    </label>
                                    <select
                                        value={sizeItems}
                                        onChange={(e) => handleSizeChange(Number(e.target.value))}
                                        aria-label="Itens por página"
                                        className="px-3 py-1 rounded-lg border border-white/50 bg-white/50 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>

                                {/* Botões de paginação */}
                                {pagination.total_pages > 1 && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setPage(page - 1)}
                                            disabled={page === 1}
                                            className="px-4 py-2 rounded-lg border border-white/50 bg-white/50 text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/70 transition-colors"
                                        >
                                            ← Anterior
                                        </button>
                                        <span className="px-4 py-2 text-text-primary flex items-center">
                                            Página {pagination.page} de {pagination.total_pages}
                                        </span>
                                        <button
                                            onClick={() => setPage(page + 1)}
                                            disabled={page >= pagination.total_pages}
                                            className="px-4 py-2 rounded-lg border border-white/50 bg-white/50 text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/70 transition-colors"
                                        >
                                            Próxima →
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompleteProductList;
